import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Avatar, Btn, ProcessStepper, Tooltip, useToast } from '@/components/ui';
import { Tour } from '@/components/tour';
import type { TourScene } from '@/components/tour';
import tourStyles from '@/components/tour/Tour.module.css';
import { MEMBER_PERMS_TOUR_STEPS } from './memberPerms/tourContent';
import { NavIcon } from '@/components/icons/NavIcon';
import { useUserGrants, useGrantProcess } from '@/hooks';
import { useFetch } from '@/hooks/useFetch';
import type { GrantCall } from '@/hooks';
import { applyGrantBatch } from '@/api/grants';
import { createGrantRequest, createRevokeRequest } from '@/api/requests';
import type { AccessGrant, Level, UserSummary } from '@/api/types';
import { ApiHttpError, getErrorMessage } from '@/api';
import { useMetaContext } from '@/components/Layout';
import { emailLocal, levelLabel } from '@/lib/format';
import { DEFAULT_STAGE_FILTER, STAGE_FILTER_OPTIONS } from '@/lib/stageFilter';
import type { StageFilterValue } from '@/lib/stageFilter';

import MemberSearchBox from './memberPerms/MemberSearchBox';
import StageGrantsCard from './memberPerms/StageGrantsCard';
import type { StageGrantActionHandle, StageGrantActionState, StageGrantSummary } from './memberPerms/StageGrantsCard';
import GameGrantList from './memberPerms/GameGrantList';
import { fetchAllProjects } from './memberPerms/useBulkGameSelection';
import HeldGamesList from './memberPerms/HeldGamesList';
import type { SelectedGame } from './memberPerms/HeldGamesList';
import HeldStagesPanel from './memberPerms/HeldStagesPanel';
import { STAGE_GRANT_GUIDE } from './memberPerms/StageGrantsPanel';
import { APP_STAGES } from '@/api/stageGrants';
import { stageLabel } from './memberPerms/StageGrantsPanel';
import { changeSummary, groupByLevel } from './memberPerms/permChanges';
import type { PermChangeEntry, PermDiff } from './memberPerms/permChanges';

import styles from './MemberPerms.module.css';

// plan §5.26: PageMemberPerms.
// 한 멤버를 선택하면 게임 목록에 그 멤버의 현재 권한이 체크 상태 + 게임별 역할로 표시된다.
// 사용자가 체크/역할을 바꾸면 기존 권한과의 diff를 계산해 부여(grant)/변경(update)/회수(revoke)를 한 번에 처리.
// Admin: 즉시 적용 — PermTab과 동일한 useGrantProcess 스텝퍼로 진행 과정 표시.
// 비Admin: createGrantRequest/createRevokeRequest 요청 흐름.

// 게임 목록 페이지 크기. 선택(draft)은 전역 state라 페이지 전환에도 유지된다.
const GAME_PAGE_SIZE = 10;

// 헤더 요약 칩: 역할(editor/viewer)과 상태(저장됨/변경 예정)가 같은 Stage를 한 칩으로 묶는다.
// 숫자 Stage는 "Stage 1, 2, 3"으로 합치고 UA Ops는 이름 그대로 뒤에 붙인다.
function groupStageChips(items: StageGrantSummary[]) {
  const order: Level[] = ['editor', 'viewer'];
  const chips: { key: string; stages: string; level: Level; pending: boolean }[] = [];
  for (const pending of [false, true]) {
    for (const level of order) {
      const group = items.filter(item => item.level === level && (item.changeKind !== 'current') === pending);
      if (!group.length) continue;
      const numbers = group.filter(item => item.stage !== 'UA_OPS').map(item => item.stage.replace('STAGE_', ''));
      const parts: string[] = [];
      if (numbers.length) parts.push(`Stage ${numbers.join(', ')}`);
      if (group.some(item => item.stage === 'UA_OPS')) parts.push(stageLabel('UA_OPS'));
      chips.push({ key: `${level}-${pending}`, stages: parts.join(', '), level, pending });
    }
  }
  return chips;
}

export default function MemberPermsPage() {
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';
  const toast = useToast();

  // 선택한 멤버는 URL에 보존해 새로고침·공유 링크에서도 같은 화면을 복원한다.
  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get('email')?.trim().toLowerCase() ?? '';
  const memberEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailParam) ? emailParam : null;
  const [searchQ, setSearchQ] = useState(memberEmail ?? '');

  useEffect(() => { setSearchQ(memberEmail ?? ''); }, [memberEmail]);

  const selectMemberEmail = (email: string | null) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (email) next.set('email', email.trim().toLowerCase());
      else next.delete('email');
      return next;
    });
  };

  // games 목록 (granting 대상) — 검색 + 페이지네이션.
  // 선택 상태(draft)는 projectId 기반 전역 state라 검색/페이지를 넘겨도 유지된다.
  const [gamePage, setGamePage] = useState(1);
  const [gameSearchQ, setGameSearchQ] = useState('');
  const [gameStages, setGameStages] = useState<StageFilterValue[]>([]);
  const [stageBusy, setStageBusy] = useState(false);
  // Stage별 권한 박스는 기본 접힘 — 자주 바꾸는 건 게임별 권한이라 화면을 게임에 양보한다. 멤버가 바뀌면 다시 접는다.
  const [stageOpen, setStageOpen] = useState(false);
  // 게임별 권한 박스도 같은 아코디언 헤더를 쓰되 기본은 펼침.
  const [gamesOpen, setGamesOpen] = useState(true);
  useEffect(() => { setStageOpen(false); setGamesOpen(true); }, [memberEmail]);

  // 사용법 안내 투어 — 버튼을 눌렀을 때만 시작. Stage 단계에서는 아코디언을 펼치고, 끝나면 시작 전 상태로 되돌린다.
  const [tourOpen, setTourOpen] = useState(false);
  const tourSnapshotRef = useRef<{ stageOpen: boolean; gamesOpen: boolean } | null>(null);
  const startTour = () => {
    tourSnapshotRef.current = { stageOpen, gamesOpen };
    setGamesOpen(true);
    window.setTimeout(() => setTourOpen(true), 60);
  };
  const handleTourScene = useCallback((scene: TourScene | null) => {
    setStageOpen(scene === 'stage' ? true : (tourSnapshotRef.current?.stageOpen ?? false));
  }, []);
  const closeTour = () => {
    setTourOpen(false);
    const snap = tourSnapshotRef.current;
    if (snap) {
      setStageOpen(snap.stageOpen);
      setGamesOpen(snap.gamesOpen);
      tourSnapshotRef.current = null;
    }
  };
  const [stageSummary, setStageSummary] = useState<StageGrantSummary[]>([]);
  const [stageActionState, setStageActionState] = useState<StageGrantActionState | null>(null);
  const stageActionRef = useRef<StageGrantActionHandle | null>(null);
  const stageOptions = STAGE_FILTER_OPTIONS;
  const selectedStages = gameStages.filter(stage => stageOptions.some(option => option.value === stage));
  // Stage 필터는 게임 목록만 좁히며, 대상 멤버의 Stage 권한과 독립적으로 동작한다.
  const effectiveStages = selectedStages.length ? selectedStages : stageOptions.map(option => option.value as StageFilterValue);
  // 필터 조건의 게임을 전부 받아 클라이언트에서 페이지네이션한다 — 보유 권한으로 넣고 뺄 때
  // 추가 목록의 건수·페이지가 즉시 맞도록 (서버 페이지에서 걷어내면 페이지가 들쭉날쭉해진다).
  const projectKey = JSON.stringify([memberEmail, gameSearchQ, effectiveStages]);
  const projects = useFetch(async signal => ({
    key: projectKey,
    result: memberEmail ? await fetchAllProjects({
      q: gameSearchQ.trim() || undefined,
      stage: effectiveStages,
    }, signal) : null,
  }), [projectKey, memberEmail]);
  useEffect(() => {
    setGamePage(1);
    // 프로젝트 목록과 같은 기본값(Stage 1 제외). 다중 선택으로 자유롭게 좁힐 수 있다.
    setGameStages([...DEFAULT_STAGE_FILTER]);
  }, [memberEmail]);

  // 검색어·필터가 바뀌면 첫 페이지로. draft는 그대로 유지된다.
  const onChangeGameSearch = (value: string) => {
    setGameSearchQ(value);
    setGamePage(1);
  };
  const onChangeGameStages = (values: StageFilterValue[]) => {
    setGameStages(values);
    setGamePage(1);
  };

  // 선택된 멤버의 권한
  const userGrants = useUserGrants(memberEmail ?? undefined);

  // 게임별 선택 상태 — project_id -> level. 키가 있으면 "그 레벨로 부여/유지", 없으면 권한 없음.
  // 멤버 선택 시 기존 권한으로 초기화되고, 사용자가 편집한 결과가 기존과의 diff가 된다.
  const [draft, setDraft] = useState<Record<string, Level>>({});


  // 비admin 권한 요청 시 필수 사유 (승인자에게 전달).
  const [reason, setReason] = useState('');

  const grantsData = userGrants.data;
  // 재조회 중에도 직전/낙관적 데이터를 유지한다. loading을 기준으로 비우면 저장 성공 뒤
  // 서버 확인 응답이 올 때까지 현재 보유 권한이 다시 '변경 예정'으로 보인다.
  const grantsLoaded = !!grantsData && grantsData.email === memberEmail && !userGrants.error;
  const hasAllScope = grantsLoaded ? grantsData.has_all_scope : false;
  const [draftTarget, setDraftTarget] = useState<string | null>(null);

  // 전체 범위와 개별 게임 의도를 구분해 전환 시 기존 권한과 비교한다.
  const existingByProject = useMemo<Record<string, Level>>(() => {
    if (!grantsLoaded) return {};
    const map: Record<string, Level> = {};
    for (const g of grantsData.grants) {
      if (g.scope !== 'all') map[g.scope] = g.level;
    }
    return map;
  }, [grantsLoaded, grantsData]);

  // 멤버가 바뀌면 draft와 기존 all 해지 상태를 초기화한다.
  useEffect(() => {
    setDraft({});
    setReason('');
    setStageSummary([]);
  }, [memberEmail]);

  // grants 로드되면 기존 권한으로 draft 채움 (email 일치 시에만 — useFetch는 로딩 중 이전 data 유지).
  useEffect(() => {
    if (!grantsLoaded) return;
    setDraft({ ...existingByProject });
    setDraftTarget(memberEmail);
  }, [grantsLoaded, existingByProject, memberEmail]);

  const pickMember = (user: UserSummary) => {
    selectMemberEmail(user.email);
    setSearchQ(user.email);
  };

  // 검색 결과에 없는 이메일도 직접 입력해 선택 가능. backend가 도메인 정책으로 거부하면 grant 시도 시 에러 토스트.
  const pickManualEmail = (email: string) => {
    selectMemberEmail(email);
    setSearchQ(email);
  };

  // 넣기/빼기 순서 기록 — 최근에 움직인 게임이 목록 맨 위에 쌓이도록 정렬에 쓴다.
  const moveSeqRef = useRef<Record<string, number>>({});
  const moveCounterRef = useRef(0);
  const [moveTick, setMoveTick] = useState(0);
  const stampMove = (projectIds: string[]) => {
    for (const projectId of projectIds) moveSeqRef.current[projectId] = ++moveCounterRef.current;
    setMoveTick(tick => tick + 1);
  };

  // 보유 권한에서 빼기 → 추가 목록으로 복귀. (넣기는 항상 역할 버튼으로만 한다.)
  const toggleProject = (projectId: string) => {
    stampMove([projectId]);
    setDraft((prev) => {
      const next = { ...prev };
      if (projectId in next) delete next[projectId];
      else next[projectId] = 'viewer';
      return next;
    });
  };

  // 게임 행: 역할 지정 (보유 권한에 없던 게임이면 그 역할로 추가)
  const setProjectLevel = (projectId: string, level: Level) => {
    if (!(projectId in draft)) stampMove([projectId]);
    setDraft((prev) => ({ ...prev, [projectId]: level }));
  };

  // 기존 권한 대비 변경 분류 — 부여/변경/회수.
  const diff = useMemo<PermDiff>(() => {
    const toGrant: PermChangeEntry[] = [];
    const toUpdate: PermChangeEntry[] = [];
    const toRevoke: PermChangeEntry[] = [];
    for (const [projectId, level] of Object.entries(draft)) {
      const prev = existingByProject[projectId];
      if (prev === undefined) toGrant.push({ projectId, level });
      else if (prev !== level) toUpdate.push({ projectId, level });
    }
    for (const [projectId, level] of Object.entries(existingByProject)) {
      if (!(projectId in draft)) toRevoke.push({ projectId, level });
    }
    return { toGrant, toUpdate, toRevoke };
  }, [draft, existingByProject]);

  const changeCount = diff.toGrant.length + diff.toUpdate.length + diff.toRevoke.length;
  // Stage 변경도 같은 버튼으로 처리한다. admin은 즉시 저장, 그 외에는 요청함에 요청을 올린다.
  const stageChangeCount = stageActionState?.changeCount ?? 0;
  const totalChangeCount = changeCount + stageChangeCount;

  // submit 진행 중 — 버튼 disabled + "적용 중..." 표시, 추가 클릭 방지.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 권한 반영 과정 스텝퍼 — PermTab과 공유하는 공통 hook.
  const { processSteps, processTitle, runGrantProcess, closeStepper } = useGrantProcess();
  const reloadGrants = () => { userGrants.reload(); projects.reload(); };

  // ── 게임별 diff 적용 ───────────────────────────────────
  const submitChanges = async () => {
    if (!memberEmail) {
      toast.error('멤버를 선택해주세요');
      return;
    }
    if (totalChangeCount === 0) {
      toast.error('변경된 권한이 없어요');
      return;
    }
    if (!isAdmin && (changeCount > 0 || stageChangeCount > 0) && !reason.trim()) {
      toast.error('변경 사유를 입력해주세요');
      return;
    }
    if (isSubmitting || stageBusy || draftTarget !== memberEmail) return;
    const me = memberEmail;
    setIsSubmitting(true);
    try {
      // 1) Stage 변경 — admin: 원장 저장 + BigQuery 반영, 그 외: 요청함에 요청.
      //    실패하면 카드 안 오류로 안내하고 게임 변경은 진행하지 않는다.
      if (stageChangeCount > 0) {
        const stageOk = isAdmin
          ? await stageActionRef.current?.submit()
          : await stageActionRef.current?.request(reason.trim());
        if (!stageOk) {
          toast.error(isAdmin
            ? 'Stage 권한 저장에 실패했어요. Stage 카드의 안내를 확인해 주세요'
            : 'Stage 권한 요청에 실패했어요. Stage 카드의 안내를 확인해 주세요');
          return;
        }
        if (!isAdmin) {
          toast.push('Stage 권한 요청을 보냈어요. 승인 후 반영됩니다', 'success', 4500, 'center');
          if (changeCount === 0) { setReason(''); return; }
        }
        if (changeCount === 0) return;
      }
      // 2) 게임별 변경
      if (isAdmin) {
        // admin: 부여/변경/회수를 순차 grantCall 배열로 묶어 공통 스텝퍼로 진행.
        const grants = [...diff.toGrant, ...diff.toUpdate].map(({ projectId, level }) => ({
          scope: projectId,
          level,
        }));
        const revokeScopes = diff.toRevoke.map(({ projectId }) => projectId);
        const calls: GrantCall[] = [() => applyGrantBatch({
          email: me,
          grants,
          revoke_scopes: revokeScopes,
        })];
        const refreshVisibleGrantState = () => {
          // mutation 성공 시 서버 재조회보다 먼저 화면의 기준 상태를 바꾼다.
          // 이어지는 reload는 낙관적 상태를 서버 원장으로 다시 검증한다.
          userGrants.mutate(previous => {
            if (!previous || previous.email !== me) return previous;
            const previousByScope = Object.fromEntries(previous.grants.map(grant => [grant.scope, grant]));
            const individual = Object.entries(draft).map(([scope, level]) => {
              const current = previousByScope[scope];
              const gameMeta = metaCacheRef.current[scope];
              return {
                ...(current ?? {}),
                scope,
                level,
                source: current?.source ?? 'admin_direct',
                request_id: current?.request_id ?? null,
                granted_at: current?.granted_at ?? new Date().toISOString(),
                granted_by: current?.granted_by ?? meta.current_user.email,
                display_name: current?.display_name ?? gameMeta?.displayName ?? null,
                prefix: current?.prefix ?? gameMeta?.prefix ?? null,
                icon_url: current?.icon_url ?? gameMeta?.iconUrl ?? null,
                os: current?.os ?? gameMeta?.os ?? [],
              } satisfies AccessGrant;
            });
            const nextGrants = hasAllScope && previousByScope.all
                ? [previousByScope.all, ...individual]
                : individual;
            return { ...previous, grants: nextGrants, has_all_scope: hasAllScope };
          });
          reloadGrants();
        };
        const ok = await runGrantProcess(
          `${emailLocal(me)}님 게임별 권한 변경 중`,
          calls,
          refreshVisibleGrantState,
        );
        if (!ok) return;
      } else {
        // member: grant/revoke 요청. 같은 level끼리 묶어 요청 수를 최소화.
        const grantLike = [...diff.toGrant, ...diff.toUpdate];
        const grantByLevel = groupByLevel(grantLike);
        for (const [level, projectIds] of Object.entries(grantByLevel)) {
          if (projectIds.length === 0) continue;
          await createGrantRequest({
            email: memberEmail,
            level: level as Level,
            scope_type: 'game',
            games: projectIds,
            reason: reason.trim(),
          });
        }
        const revokeByLevel = groupByLevel(diff.toRevoke);
        for (const [level, projectIds] of Object.entries(revokeByLevel)) {
          if (projectIds.length === 0) continue;
          await createRevokeRequest({
            email: memberEmail,
            level: level as Level,
            scope_type: 'game',
            games: projectIds,
            reason: reason.trim(),
          });
        }
        setDraft({ ...existingByProject });
        setReason('');
        toast.push(`권한 변경 요청을 보냈어요 (${changeSummary(diff)}). 승인 후 반영됩니다`, 'success', 4500, 'center');
      }
    } catch (err) {
      // Admin 직접 반영은 일부 성공할 수 있어 재조회한다. 일반 요청 실패는
      // 실제 권한을 바꾸지 않으므로 선택과 사유를 유지해 다시 요청할 수 있게 한다.
      if (isAdmin) reloadGrants();
      closeStepper();
      const message = err instanceof ApiHttpError && err.status === 403
        ? 'Admin이 아니므로 이 동작을 직접 실행할 수 없어요'
        : getErrorMessage(err);
      if (isAdmin) toast.error(message);
      else toast.push(`권한 변경 요청에 실패했어요. ${message}`, 'error', 4500, 'center');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentProjects = projects.data?.key === projectKey ? projects.data.result : null;
  const allProjects = currentProjects ?? [];
  // 전송 목록 UX: 보유 권한 박스는 "저장하면 갖게 될 게임"(draft)만 담고, 그 외는 추가 목록에 있다.
  // 저장된 게임을 빼면 추가 목록으로 돌아가 '회수 예정' 표시가 붙고, 다시 넣으면 회수가 취소된다.
  // 저장된 게임(existing)은 보유 박스에 있거나(draft 포함) 상단 '회수 예정' 행에 있으므로 일반 목록에서는 뺀다.
  const addableProjects = useMemo(() => allProjects.filter(project => {
    const projectId = project.project_id;
    return projectId === null || (!(projectId in draft) && !(projectId in existingByProject));
  }), [allProjects, draft, existingByProject]);
  const hiddenFromAddList = allProjects.length - addableProjects.length;
  const totalProjects = addableProjects.length;
  const totalGamePages = Math.max(1, Math.ceil(totalProjects / GAME_PAGE_SIZE));
  // 넣고 빼서 페이지 수가 줄면 현재 페이지를 마지막 페이지로 당긴다.
  useEffect(() => { setGamePage(current => Math.min(current, totalGamePages)); }, [totalGamePages]);
  const currentGamePage = Math.min(gamePage, totalGamePages);
  const visibleProjects = useMemo(
    () => addableProjects.slice((currentGamePage - 1) * GAME_PAGE_SIZE, currentGamePage * GAME_PAGE_SIZE),
    [addableProjects, currentGamePage],
  );
  // 모두 넣기 — 이미 받아둔 목록에서 보유 권한에 없는 게임을 고른 역할로 모두 넣는다.
  const selectAllAddable = (level: Level) => {
    if (dimmed) return;
    const selectable = addableProjects.filter(game => game.project_id);
    for (const game of selectable) {
      metaCacheRef.current[game.project_id!] = {
        prefix: game.prefix, displayName: game.display_name, iconUrl: game.icon_url, os: game.os, stage: game.stage ?? null,
      };
    }
    stampMove(selectable.filter(game => !(game.project_id! in draft)).map(game => game.project_id!));
    setDraft(previous => {
      const next = { ...previous };
      for (const game of selectable) next[game.project_id!] ??= level;
      return next;
    });
  };
  const grantSelectedCount = Object.keys(draft).length;
  const dimmed = !memberEmail || stageBusy || isSubmitting || !grantsLoaded || draftTarget !== memberEmail;

  // 게임 메타 맵(project_id → 표시 정보). 기존 부여 게임은 grants enrich 메타로,
  // 새로 선택한 게임은 현재 페이지(visibleProjects) 메타로 채운다 → 상단 박스가 페이지와 무관하게 표시 가능.
  //
  // 누적 캐시(ref): 검색/페이지를 넘기면 visibleProjects가 바뀌는데, 이전에 선택해둔 게임이
  // 현재 목록에도 없고 기존 grants에도 없으면 메타가 사라져 상단 박스가 project_id로만 표시되던 버그가 있었다.
  // 한 번 본 게임 메타를 ref에 누적해 검색/페이지 전환에도 유지한다.
  type GameMeta = { prefix: string; displayName: string | null; iconUrl: string | null; os: string[]; stage: string | null };
  const metaCacheRef = useRef<Record<string, GameMeta>>({});
  const metaById = useMemo<Record<string, GameMeta>>(() => {
    const m = metaCacheRef.current;
    if (grantsLoaded && grantsData) {
      for (const g of grantsData.grants) {
        if (g.scope !== 'all' && g.prefix) {
          m[g.scope] = {
            prefix: g.prefix,
            displayName: g.display_name ?? null,
            iconUrl: g.icon_url ?? null,
            os: g.os ?? [],
            // grants enrich에는 Stage가 없다 — 목록에서 본 값이 있으면 유지한다.
            stage: m[g.scope]?.stage ?? null,
          };
        }
      }
    }
    for (const p of allProjects) {
      if (p.project_id) {
        m[p.project_id] = {
          prefix: p.prefix,
          displayName: p.display_name,
          iconUrl: p.icon_url,
          os: p.os,
          stage: p.stage ?? null,
        };
      }
    }
    // 참조를 새로 만들어 consumer(selectedEntries 등)가 갱신을 감지하게 한다.
    return { ...m };
  }, [grantsLoaded, grantsData, allProjects, draft]);

  // 현재 권한과 변경 예정 상태. 회수 예정(revoke)은 보유 박스가 아니라 추가 목록 상단에 고정 표시한다.
  const allEntries = useMemo<SelectedGame[]>(() => {
    if (draftTarget !== memberEmail) return [];
    const order = { grant: 0, revoke: 1, update: 2, current: 3 } as const;
    const projectIds = new Set([...Object.keys(existingByProject), ...Object.keys(draft)]);
    return [...projectIds]
      .map(projectId => {
        const meta = metaById[projectId];
        const existing = existingByProject[projectId];
        const selected = draft[projectId];
        const changeKind: SelectedGame['changeKind'] =
          existing === undefined ? 'grant'
            : selected === undefined ? 'revoke'
              : existing !== selected ? 'update' : 'current';
        return {
          projectId,
          prefix: meta?.prefix ?? projectId,
          displayName: meta?.displayName ?? null,
          iconUrl: meta?.iconUrl ?? null,
          os: meta?.os ?? [],
          stage: meta?.stage ?? null,
          level: (selected ?? existing)!,
          changeKind,
        };
      })
      .sort((a, b) => {
        if (order[a.changeKind] !== order[b.changeKind]) return order[a.changeKind] - order[b.changeKind];
        // 저장 전 변경(추가 예정·권한 회수)은 최근에 움직인 것이 위로, 저장된 항목은 이름순.
        if (a.changeKind === 'grant' || a.changeKind === 'revoke') {
          return (moveSeqRef.current[b.projectId] ?? 0) - (moveSeqRef.current[a.projectId] ?? 0);
        }
        return (a.displayName || a.prefix).localeCompare(b.displayName || b.prefix);
      });
  // moveTick: 순서 기록이 바뀌면 다시 정렬한다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, metaById, existingByProject, draftTarget, memberEmail, moveTick]);
  const selectedEntries = useMemo(() => allEntries.filter(entry => entry.changeKind !== 'revoke'), [allEntries]);
  // 검색·페이지와 무관하게 항상 보이도록 추가 목록 위에 고정한다 — 숨은 회수가 없게.
  const pendingRevokes = useMemo(() => allEntries.filter(entry => entry.changeKind === 'revoke'), [allEntries]);

  // 기존 권한의 회수 취소는 새 부여가 아니라 직전 상태 복원이므로 허용한다.
  const restoreExistingGrant = (projectId: string, level: Level) => {
    setDraft(previous => ({ ...previous, [projectId]: level }));
  };

  const clearNewSelections = () => {
    setDraft(previous => Object.fromEntries(
      Object.entries(previous).filter(([projectId]) => existingByProject[projectId] !== undefined),
    ));
  };

  const clearExistingSelections = () => {
    setDraft(previous => Object.fromEntries(
      Object.entries(previous).filter(([projectId]) => existingByProject[projectId] === undefined),
    ));
  };

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        {/* 멤버를 고르기 전에는 제목·검색창만 화면 중앙에 둔다. 고르면 transform으로 위로 올라오고 아래 박스들이 순서대로 나타난다. */}
        <div className={`${styles.entry} ${memberEmail ? '' : styles.entryHero}`} data-state={memberEmail ? 'ready' : 'hero'}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>멤버별 권한</h1>
            {memberEmail && <button type="button" className={tourStyles.tourBtn} onClick={startTour}>사용법 안내</button>}
          </div>
          {!memberEmail && <p className={styles.heroHint}>권한을 확인하거나 바꿀 멤버의 이메일을 검색하세요.</p>}
          <div data-tour="member-search">
            <MemberSearchBox
              searchQ={searchQ}
              onChangeSearchQ={setSearchQ}
              onPickMember={pickMember}
              onPickManualEmail={pickManualEmail}
              autoFocus={!memberEmail}
            />
          </div>
        </div>

        {memberEmail && <div className={`${styles.memberContext} ${styles.reveal}`} data-tour="member-profile">
              <Avatar email={memberEmail} name={emailLocal(memberEmail)} size={44} />
              <div className={styles.memberInfo}>
                <div>
                  <span className={styles.memberName}>{emailLocal(memberEmail)}</span>
                  {/* 프로필 요약 태그: 저장된 Stage 수 · 개별 게임 수. Stage를 전부 갖거나 기존 all 권한이면 '모든 게임 권한' 하나로. */}
                  {(() => {
                    if (!grantsLoaded) return <span className={styles.memberBadge}>...</span>;
                    const savedStages = stageSummary.filter(item => item.changeKind !== 'grant').length;
                    if (hasAllScope || (stageActionState?.loaded && savedStages >= APP_STAGES.length)) {
                      return <span className={`${styles.memberBadge} ${styles.memberBadgeAll}`}>모든 게임 권한</span>;
                    }
                    return <>
                      {savedStages > 0 && <span className={styles.memberBadge}>Stage {savedStages}개</span>}
                      <span className={styles.memberBadge}>개별 게임 {Object.keys(existingByProject).length}개</span>
                    </>;
                  })()}
                </div>
                <div className={styles.memberMeta}>{memberEmail}</div>
              </div>
        </div>}


        {memberEmail && userGrants.error && <p role="alert">{getErrorMessage(userGrants.error)} <Btn variant="ghost" onClick={userGrants.reload}>권한 다시 조회</Btn></p>}
        {memberEmail && projects.error && <p role="alert">{getErrorMessage(projects.error)} <Btn variant="ghost" onClick={projects.reload}>게임 다시 조회</Btn></p>}

        {/* 비admin 권한 요청 필수 사유 */}
        {!isAdmin && memberEmail && (
          <div className={styles.reasonCard}>
            <div className={styles.reasonHead}>
              <span className={styles.reasonLabel}>변경 사유</span>
              <span className={styles.reasonRequired}>필수</span>
            </div>
            <textarea
              className={styles.reasonInput}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="권한 변경 사유를 입력하세요 — 승인자에게 전달됩니다"
              rows={2}
            />
          </div>
        )}

        {/* ── Stage별 권한: 접힌 아코디언. 안에 권한 추가(왼쪽) · 보유 권한(오른쪽) ── */}
        {memberEmail && (() => {
          const heldStageSummary = stageSummary.filter(item => item.changeKind !== 'revoke');
          return <section className={`${styles.groupBox} ${styles.reveal} ${styles.revealDelay1}`} aria-label="Stage별 권한" data-tour="stage-box">
            {/* 헤더: [제목][?][요약 칩] … [접기/펼치기]. 헤더 전체가 토글, '?'만 예외. */}
            <button type="button" className={styles.groupSummary} aria-expanded={stageOpen}
              aria-controls="stage-permission-body" onClick={() => setStageOpen(open => !open)}
              disabled={stageBusy} aria-busy={stageBusy || undefined}>
              <h2 className={styles.groupTitle}>Stage별 권한</h2>
              <span className={styles.groupGuide} onClick={event => event.stopPropagation()}>
                <Tooltip text={STAGE_GRANT_GUIDE} />
              </span>
              {/* 접혀 있어도 무엇을 갖고 있는지 보이게 — 보유 Stage와 변경 건수 요약 */}
              <span className={styles.groupChips} aria-label="Stage 권한 요약">
                  {grantsLoaded && heldStageSummary.length === 0 && stageChangeCount === 0
                    && <span className={styles.groupChipMuted}>보유 없음</span>}
                  {/* 같은 역할·같은 상태(저장됨/변경 예정)끼리 한 칩으로 묶는다: "Stage 1, 2, 3 · Editor" */}
                  {groupStageChips(heldStageSummary).map(chip => <span key={chip.key}
                    className={`${styles.groupChip} ${chip.pending ? styles.groupChipPending : ''}`}>
                    {chip.stages} · {levelLabel(chip.level)}
                  </span>)}
                  {stageChangeCount > 0 && <span className={styles.groupChipChange}>변경 {stageChangeCount}</span>}
              </span>
              <span className={`${styles.groupChevron} ${stageOpen ? styles.groupChevronOpen : ''}`} aria-hidden="true">
                <NavIcon name="chev-d" size={16} />
              </span>
            </button>
            <div id="stage-permission-body" className={styles.transferColumns} hidden={!stageOpen}>
              <div className={styles.transferColumn} data-tour="stage-add">
                <StageGrantsCard
                  email={memberEmail}
                  actionRef={stageActionRef}
                  onBusyChange={setStageBusy}
                  disabled={!memberEmail || !grantsLoaded || isSubmitting}
                  onSaved={() => userGrants.reload()}
                  onSummaryChange={setStageSummary}
                  onActionStateChange={setStageActionState}
                  existingGrantWarning={Object.keys(existingByProject).length
                      ? '기존 게임별 권한은 Stage 설정과 관계없이 유지됩니다.'
                      : undefined}
                />
              </div>
              <div className={styles.transferColumn} data-tour="stage-held">
                <HeldStagesPanel
                  stages={stageSummary}
                  stageActionState={stageActionState}
                  disabled={dimmed}
                  onSetStage={(stage, level) => stageActionRef.current?.setStage(stage, level)}
                  onToggleStage={stage => stageActionRef.current?.toggleStage(stage)}
                  onApplyStages={() => { void stageActionRef.current?.submit(); }}
                  onRetryStages={() => stageActionRef.current?.reload()}
                />
              </div>
            </div>
          </section>;
        })()}

        {/* ── 게임별 권한: 권한 추가(왼쪽) · 보유 권한(오른쪽) ── */}
        {memberEmail && <section className={`${styles.groupBox} ${styles.reveal} ${styles.revealDelay2}`} aria-label="게임별 권한" data-tour="game-box">
          <button type="button" className={styles.groupSummary} aria-expanded={gamesOpen}
            aria-controls="game-permission-body" onClick={() => setGamesOpen(open => !open)}>
            <h2 className={styles.groupTitle}>게임별 권한</h2>
            <span className={styles.groupGuide} onClick={event => event.stopPropagation()}>
              <Tooltip text="게임별 권한은 Stage 변경과 무관하게 그 게임에 계속 유지됩니다. 왼쪽에서 게임을 넣고 오른쪽에서 역할을 정하거나 뺀 뒤, 하단의 변경 적용을 누르세요." />
            </span>
            <span className={styles.groupChips}>
              {grantsLoaded && <span className={styles.groupChip}>보유 {selectedEntries.length}</span>}
              {changeCount > 0 && <span className={styles.groupChipChange}>변경 {changeCount}</span>}
            </span>
            <span className={`${styles.groupChevron} ${gamesOpen ? styles.groupChevronOpen : ''}`} aria-hidden="true">
              <NavIcon name="chev-d" size={16} />
            </span>
          </button>
          <div id="game-permission-body" hidden={!gamesOpen}>
          {hasAllScope && <p className={styles.legacyAllNotice} role="status">
            기존 전체 게임 권한이 별도로 유지됩니다. 아래 게임별 권한을 모두 해제해도 전체 게임 권한은 회수되지 않습니다.
          </p>}
          <div className={styles.transferColumns}>
            <div className={styles.transferColumn} data-tour="game-add">
              <GameGrantList
                loading={!!memberEmail && (projects.loading || (!projects.error && !currentProjects))}
                projects={visibleProjects}
                hiddenCount={hiddenFromAddList}
                pendingRevokes={pendingRevokes}
                onRestoreRevoke={restoreExistingGrant}
                hasAllScope={false}
                disabled={!memberEmail || stageBusy || isSubmitting || !grantsLoaded || draftTarget !== memberEmail}
                allScopeLevel={null}
                onAddProjectWithLevel={setProjectLevel}
                searchQ={gameSearchQ}
                onChangeSearchQ={onChangeGameSearch}
                stages={selectedStages}
                stageOptions={stageOptions}
                onChangeStages={onChangeGameStages}
                onSelectAll={selectAllAddable}
                bulkLoading={false}
                bulkError={null}
                total={totalProjects}
                page={currentGamePage}
                pageSize={GAME_PAGE_SIZE}
                onPageChange={setGamePage}
              />
            </div>
            <div className={styles.transferColumn} data-tour="game-held">
              <HeldGamesList
                key={memberEmail ?? 'unselected'}
                games={selectedEntries}
                disabled={dimmed}
                onSetLevel={setProjectLevel}
                onRemove={toggleProject}
                onClearAdditions={clearNewSelections}
                onClearExisting={clearExistingSelections}
              />
            </div>
          </div>
          </div>
        </section>}
      </div>

      {/* 하단 액션바 — 페이지 유일의 적용 버튼. Stage 변경 → 게임 변경 순으로 저장 */}
      {memberEmail && (
        <div className={styles.actionBar}>
          <div className={styles.actionInner} data-tour="apply-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <span className={styles.actionLabel}>
                {totalChangeCount ? '변경 예정' : `현재 ${grantSelectedCount}개`}
              </span>
              {totalChangeCount > 0 && (
                <>
                  <div style={{ width: 1, height: 18, background: 'var(--sc-ink-9, #e0e0e0)' }} />
                  <span className={styles.diffSummary}>
                    {[stageChangeCount > 0 ? `Stage ${stageChangeCount}` : '', changeSummary(diff)].filter(Boolean).join(' · ')}
                  </span>
                </>
              )}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className={`${styles.actionNote} ${isAdmin ? styles.admin : styles.member}`}>
                {isAdmin ? 'Admin · 즉시 적용' : '승인 후 적용'}
              </span>
              <button
                type="button"
                className={styles.submitBtn}
                onClick={() => void submitChanges()}
                disabled={!grantsLoaded || draftTarget !== memberEmail || totalChangeCount === 0 || isSubmitting || stageBusy}
                style={isSubmitting ? { cursor: 'wait', opacity: 0.7 } : undefined}
              >
                <NavIcon name={isSubmitting ? 'check' : 'plus'} size={14} color="#fff" />
                {isSubmitting ? '적용 중...' : isAdmin ? '변경 적용' : '변경 요청'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tourOpen && <Tour steps={MEMBER_PERMS_TOUR_STEPS} onClose={closeTour} onSceneChange={handleTourScene} />}

      {/* 권한 반영 과정 스텝퍼 — admin 즉시 적용 시 단계별 진행/에러 표시 */}
      <ProcessStepper steps={processSteps} title={processTitle} onClose={closeStepper} />
    </div>
  );
}
